import React, { useState, useEffect } from 'react';
import { 
  DollarSign, Users, Clock, MapPin, Plus, Check, Calculator, 
  ChevronDown, ChevronUp, Sparkles, Info
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { getPhoneDisplay, getPhoneTel } from '@/lib/contactInfo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PricingCalculator = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  
  // Selection state
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedTravel, setSelectedTravel] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/pricing-config`);
        if (response.data && Object.keys(response.data).length > 0) {
          setConfig(response.data);
        }
      } catch (error) {
        console.error('Error fetching pricing config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const toggleAddOn = (addOnId) => {
    setSelectedAddOns(prev => 
      prev.includes(addOnId) 
        ? prev.filter(id => id !== addOnId)
        : [...prev, addOnId]
    );
  };

  const calculateTotal = () => {
    if (!config || !selectedPackage || !selectedDuration) return null;

    const pkg = config.packages.find(p => p.id === selectedPackage);
    const duration = config.durationMultipliers.find(d => d.id === selectedDuration);
    const travel = config.travelFees.find(t => t.id === selectedTravel);
    
    if (!pkg || !duration) return null;

    let total = pkg.basePrice * duration.multiplier;
    
    // Add travel fee
    if (travel && typeof travel.fee === 'number') {
      total += travel.fee;
    }
    
    // Add add-ons
    selectedAddOns.forEach(addOnId => {
      const addOn = config.addOns.find(a => a.id === addOnId);
      if (addOn) {
        total += addOn.price;
      }
    });
    
    // Apply minimum
    if (total < config.settings.minimumBookingFee) {
      total = config.settings.minimumBookingFee;
    }

    return total;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!config || !config.settings?.showPricesPublicly) {
    return (
      <div className="bg-gradient-to-br from-primary/10 to-orange-500/10 border border-primary/30 rounded-2xl p-8 text-center">
        <DollarSign className="w-12 h-12 text-primary mx-auto mb-4" />
        <h3 className="text-xl font-bold mb-2">Custom Pricing Available</h3>
        <p className="text-muted-foreground mb-4">
          Every event is unique! Fill out the quote form above for a personalized estimate.
        </p>
        <p className="text-sm text-primary font-medium">
          Or call us: <a href={getPhoneTel()} className="underline">{getPhoneDisplay()}</a>
        </p>
      </div>
    );
  }

  const total = calculateTotal();
  const deposit = total ? total * (config.settings.depositPercentage / 100) : null;

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" id="pricing">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-6 flex items-center justify-between bg-gradient-to-r from-green-500/10 to-emerald-500/10 hover:from-green-500/20 hover:to-emerald-500/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Calculator className="w-6 h-6 text-green-400" />
          <div className="text-left">
            <h3 className="text-xl font-bold">Pricing Calculator</h3>
            <p className="text-sm text-muted-foreground">Estimate your event cost</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-6 space-y-6">
              {/* Package Selection */}
              <div>
                <label className="block text-sm font-bold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Select Group Size
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {config.packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => setSelectedPackage(pkg.id)}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        selectedPackage === pkg.id
                          ? 'bg-primary/20 border-primary'
                          : 'bg-background border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{pkg.name}</span>
                        {selectedPackage === pkg.id && <Check className="w-4 h-4 text-primary" />}
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{pkg.description}</p>
                      <p className="text-lg font-bold text-primary">
                        ${pkg.basePrice.toLocaleString()}
                        <span className="text-xs font-normal text-muted-foreground"> base</span>
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Duration Selection */}
              <div>
                <label className="block text-sm font-bold mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-green-400" />
                  Performance Duration
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {config.durationMultipliers.map((dur) => (
                    <button
                      key={dur.id}
                      onClick={() => setSelectedDuration(dur.id)}
                      className={`p-3 rounded-xl border text-center transition-all ${
                        selectedDuration === dur.id
                          ? 'bg-green-500/20 border-green-500'
                          : 'bg-background border-border hover:border-green-500/50'
                      }`}
                    >
                      <span className="text-sm font-medium">{dur.name}</span>
                      <p className="text-xs text-muted-foreground mt-1">{dur.multiplier}x</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Travel Fees */}
              <div>
                <label className="block text-sm font-bold mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-400" />
                  Event Location
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {config.travelFees.map((travel) => (
                    <button
                      key={travel.id}
                      onClick={() => setSelectedTravel(travel.id)}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        selectedTravel === travel.id
                          ? 'bg-blue-500/20 border-blue-500'
                          : 'bg-background border-border hover:border-blue-500/50'
                      }`}
                    >
                      <span className="text-sm font-medium">{travel.name}</span>
                      <p className="text-xs text-muted-foreground mt-1">
                        {typeof travel.fee === 'number' 
                          ? travel.fee === 0 ? 'No travel fee' : `+$${travel.fee}`
                          : travel.fee
                        }
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Add-Ons */}
              <div>
                <label className="block text-sm font-bold mb-3 flex items-center gap-2">
                  <Plus className="w-4 h-4 text-orange-400" />
                  Add-On Services (Optional)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {config.addOns.map((addOn) => (
                    <button
                      key={addOn.id}
                      onClick={() => toggleAddOn(addOn.id)}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        selectedAddOns.includes(addOn.id)
                          ? 'bg-orange-500/20 border-orange-500'
                          : 'bg-background border-border hover:border-orange-500/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{addOn.name}</span>
                        {selectedAddOns.includes(addOn.id) && <Check className="w-4 h-4 text-orange-400" />}
                      </div>
                      <p className="text-xs text-muted-foreground">{addOn.description}</p>
                      <p className="text-sm font-bold text-orange-400 mt-1">+${addOn.price}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Personnel Breakdown */}
              <div className="bg-background rounded-xl p-4 border border-border">
                <h4 className="font-bold mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  Personnel Rates (for reference)
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {config.personnel.map((person) => (
                    <div key={person.id} className="text-sm">
                      <p className="font-medium">{person.name}</p>
                      <p className="text-primary font-bold">${person.pricePerHour}/hr</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total Calculation */}
              {total !== null && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-primary/20 to-orange-500/20 border border-primary/30 rounded-xl p-6"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Estimated Total</p>
                      <p className="text-4xl font-bold text-foreground">
                        ${total.toLocaleString()}
                      </p>
                      {deposit && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Deposit required: <span className="text-primary font-bold">${deposit.toLocaleString()}</span> ({config.settings.depositPercentage}%)
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <a
                        href="#quote-form"
                        className="px-6 py-3 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-colors text-center"
                      >
                        Get Official Quote
                      </a>
                      <a
                        href={getPhoneTel()}
                        className="px-6 py-2 bg-stone-700 text-white rounded-xl hover:bg-stone-600 transition-colors text-center text-sm"
                      >
                        Call: {getPhoneDisplay()}
                      </a>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Disclaimer */}
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>{config.settings.disclaimerText}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PricingCalculator;
